import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TooltipProvider } from './components/ui/tooltip';
import './styles/studio.css';
import './styles/batch.css';
import './styles/agent.css';
import './styles/top-nav-tiered.css';
import './styles/auth.css';
import './styles/canvas-bg.css';
import './styles/media-states.css';
import './styles/credits-banner.css';
import './styles/admin.css';
import './styles/node-tooltip.css';
import './styles/lumiere.css';
import './styles/directors.css';
import './styles/reveal.css';
import './styles/promptfield.css';
import './styles/lumiere-entry.css';
import './styles/social.css';

// Studio components
import { Canvas, ToolPalette, AssetHistory, TopNavBar, NodeProperties, ExecutionsPanel, SaveConflictModal } from './components/studio';
import BatchPanel from './components/studio/BatchPanel';
import NewVersionBanner from './components/NewVersionBanner';
import DirectorsComposer from './components/studio/DirectorsComposer';
import TimelineEditor from './components/timeline/TimelineEditor';
import LumiereEntryOverlay from './components/studio/LumiereEntryOverlay';
import LmxLayer from './lmx/LmxLayer';
import LmxDock from './lmx/LmxDock';
import LmxLab from './lmx/LmxLab';
import { useLmxAvailability } from './lmx/useLmxAvailability';
// Envío UI-2 · Vista Canvas / Vista Edición (detrás de flag, ver layout/layoutV2.js)
import { LAYOUT_V2 } from './layout/layoutV2';
import './styles/layout-v2.css';

/**
 * UI-2 · con el flag prendido: escenario (canvas + editor encima) a la
 * izquierda y LMX como columna fija a la derecha. Apagado: no cambia nada.
 */
const V2Shell = ({ stage, side, children }) => {
  React.useEffect(() => {
    if (!LAYOUT_V2) return undefined;
    document.body.classList.add('sml-v2');
    return () => document.body.classList.remove('sml-v2');
  }, []);
  if (!LAYOUT_V2) return children;
  return (
    <div className="sml-v2-row">
      <div className="sml-v2-stage">
        {children}
        {stage}
      </div>
      {side}
    </div>
  );
};

const LMX_LAB = String(process.env.REACT_APP_LMX_LAB ?? 'false').toLowerCase() === 'true';
const LMX_DOCK = String(process.env.REACT_APP_LMX_DOCK ?? 'true') !== 'false';

/**
 * La ruta /lmx-lab (laboratorio de la capa LMX) nunca va a producción: si la
 * variable de entorno no está, esta línea se borra del build. Dentro del
 * Studio la seguridad real la hace el backend (allowlist + lmx_enabled); el
 * lab no habla con la API.
 */
import MediaPreviewModal from './components/studio/MediaPreviewModal';
import NodeBusyConfirmDialog from './components/studio/NodeBusyConfirmDialog';
import LmxTourGate from './lmx/LmxTourGate';
import LearningGate from './components/learning/LearningGate';
import ClaudeAccessGate from './components/claude/ClaudeAccessGate';
import { useNodeInFlightSync } from './hooks/useNodeInFlightSync';

// E6f · recorrido de LMX. REACT_APP_LMX_TOUR=false lo apaga del todo.
const LMX_TOUR = String(process.env.REACT_APP_LMX_TOUR ?? 'true') !== 'false';
import { useWorkflowStore } from './store/workflowStore';
import { usePreviewStore } from './store/previewStore';

// Auth components
import { ProtectedRoute, AuthSetup } from './components/auth';

// Pages
import LandingPage from './pages/LandingPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import SettingsPage from './pages/SettingsPage';
import UploadLinkPage from './pages/UploadLinkPage';

/**
 * Studio - Main application component (protected)
 */
const Studio = () => {
  const showBadge = useWorkflowStore((state) => state.showBadge);
  const selectedNode = useWorkflowStore((state) => state.selectedNode);
  // E6c.5 · reconstruye desde el servidor qué nodos están generando (sobrevive
  // a recargas y a los cambios de pestaña).
  useNodeInFlightSync();
  const [rightTab, setRightTab] = React.useState('assets');
  const [rightCollapsed, setRightCollapsed] = React.useState(() => {
    try { return localStorage.getItem('rightPanelCollapsed') === '1'; } catch { return false; }
  });
  const toggleRight = () => {
    setRightCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('rightPanelCollapsed', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };
  
  // Media preview modal state
  const { isOpen, url, type, title, closePreview } = usePreviewStore();

  // El "Ver" del aviso de galería trae el panel derecho a la vista.
  React.useEffect(() => {
    const onOpen = () => {
      setRightTab('assets');
      setRightCollapsed(false);
      try { localStorage.setItem('rightPanelCollapsed', '0'); } catch (_e) {}
    };
    window.addEventListener('gallery:open', onOpen);
    return () => window.removeEventListener('gallery:open', onOpen);
  }, []);

  // E6 · LMX: el backend decide si este usuario ve el agente nuevo.
  const lmx = useLmxAvailability();

  return (
    <div className={`studio-container ${LAYOUT_V2 ? 'sml-v2' : ''}`} data-testid="studio-container">
      {/* Top Navigation */}
      <TopNavBar />

      {/* E1 — aviso de guardado concurrente (otra pestaña o el agente MCP) */}
      <SaveConflictModal />

      {/* E6c.5 — "este nodo ya está generando": mandar otra / detener / cancelar */}
      <NodeBusyConfirmDialog />

      {/* Main Studio Area */}
      <V2Shell
        stage={<TimelineEditor />}
        side={lmx.available ? <LmxDock /> : null}
      >
      <div className="studio-main">
        {/* Left Sidebar - Tool Palette */}
        <ToolPalette />

        {/* Center - Canvas */}
        <Canvas />

        {/* Right Panel */}
        <div className={`right-panel ${rightCollapsed ? 'is-collapsed' : ''}`}>
          <button
            className="sidebar-toggle right"
            onClick={toggleRight}
            title={rightCollapsed ? 'Mostrar panel derecho' : 'Ocultar panel derecho'}
            aria-label={rightCollapsed ? 'Expandir panel derecho' : 'Contraer panel derecho'}
            data-testid="right-panel-toggle"
          >
            {rightCollapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </button>
          {selectedNode ? (
            <NodeProperties />
          ) : (
            <>
              <div className="right-panel-tabs" data-testid="right-panel-tabs">
                <button
                  className={`rp-tab ${rightTab === 'assets' ? 'active' : ''}`}
                  onClick={() => setRightTab('assets')}
                  data-testid="tab-assets"
                >
                  Assets
                </button>
                <button
                  className={`rp-tab ${rightTab === 'executions' ? 'active' : ''}`}
                  onClick={() => setRightTab('executions')}
                  data-testid="tab-executions"
                >
                  Ejecuciones
                </button>
                <button
                  className={`rp-tab ${rightTab === 'batch' ? 'active' : ''}`}
                  onClick={() => setRightTab('batch')}
                  data-testid="tab-batch"
                >
                  Lote
                </button>
              </div>
              {rightTab === 'assets' && <AssetHistory />}
              {rightTab === 'executions' && <ExecutionsPanel />}
              {rightTab === 'batch' && <BatchPanel />}
            </>
          )}
        </div>
      </div>
      </V2Shell>

      {/* Configurable Custom Badge (UI-2: fuera del estudio) */}
      {showBadge && !LAYOUT_V2 && (
        <a
          href="https://www.linkedin.com/in/sergio-murillo-automatizaciones-make-zapier-inteliautomake/"
          target="_blank"
          rel="noopener noreferrer"
          className="custom-badge"
          title="Visit Sergio Murillo's LinkedIn - sergiomurillo.com.ar"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_intelifotos-ai/artifacts/pweidlbz_SOLO%20logointeliautomake.png"
            alt="InteliAutoMake Logo"
            className="badge-logo"
          />
          <span className="badge-text">created by Sergio Murillo</span>
        </a>
      )}

      {/* Global Media Preview Modal */}
      <MediaPreviewModal
        isOpen={isOpen}
        onClose={closePreview}
        url={url}
        type={type}
        title={title}
      />

      {/* Timeline Editor — sliding bottom panel */}
      {!LAYOUT_V2 && <TimelineEditor />}

      {/* E6 · LMX — si está activo para el usuario, reemplaza la entrada legacy.
          LmxLayer (capa flotante) queda como respaldo detrás del flag. */}
      {lmx.available && !LAYOUT_V2 && (LMX_DOCK ? <LmxDock /> : <LmxLayer />)}
      {lmx.available && LMX_TOUR && <LmxTourGate />}

      {/* Envío AP-1 · capítulos de aprendizaje (inicio, «?» y fin del recorrido) y reportes */}
      {lmx.ready && <LearningGate lmxAvailable={lmx.available} />}

      {/* Envío MC-2 · Claude (MCP) pide permiso para editar un proyecto hecho en la app */}
      <ClaudeAccessGate />

      {/* Entrada con Lumière legacy (sólo cuando LMX está apagado) */}
      {lmx.ready && !lmx.available && <LumiereEntryOverlay />}

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        theme="dark"
        richColors
        offset={{ bottom: 76 }}
        toastOptions={{
          style: {
            background: '#1a1a2e',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            color: '#fff',
          },
        }}
      />
    </div>
  );
};
const App = () => {
  // Check if Clerk is available (key is set). Public routes render without
  // waiting for Clerk; protected routes gate themselves inside <ProtectedRoute>.
  const clerkEnabled = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

  // If Clerk is not enabled, just show the studio directly
  if (!clerkEnabled) {
    return (
      <TooltipProvider delayDuration={500} skipDelayDuration={100}>
        <NewVersionBanner />
        <Studio />
        <DirectorsComposer />
      </TooltipProvider>
    );
  }

  // NOTE: we intentionally do NOT gate the whole Routes tree on Clerk's
  // `isLoaded`. Public routes (landing, sign-in, sign-up) render just fine
  // while Clerk boots in the background. Protected routes have their own
  // loading state via <ProtectedRoute>.

  return (
    <TooltipProvider delayDuration={500} skipDelayDuration={100}>
      <NewVersionBanner />
      <Routes>
        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* LMX Lab — sólo desarrollo (REACT_APP_LMX_LAB=true, no existe en el
            build de producción) */}
        {LMX_LAB && <Route path="/lmx-lab" element={<LmxLab />} />}



        {/* Envío MC-3 · subir un archivo que pidió Claude: SIN login (el enlace es la llave) */}
        <Route path="/subir/:ticket" element={<UploadLinkPage />} />

        {/* Auth routes */}
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />

        {/* Payment routes (protected) */}
        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <AuthSetup>
                <PaymentSuccess />
              </AuthSetup>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/cancel"
          element={
            <ProtectedRoute>
              <AuthSetup>
                <PaymentCancel />
              </AuthSetup>
            </ProtectedRoute>
          }
        />

        {/* Configuración (protegida) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AuthSetup>
                <SettingsPage />
              </AuthSetup>
            </ProtectedRoute>
          }
        />

        {/* Protected studio route - wrapped with AuthSetup for token injection */}
        <Route
          path="/studio/*"
          element={
            <ProtectedRoute>
              <AuthSetup>
                <Studio />
                <DirectorsComposer />
              </AuthSetup>
            </ProtectedRoute>
          }
        />
      </Routes>
    </TooltipProvider>
  );
};

export default App;
